import keycloak, { login } from "../auth/keycloak";

const API_BASE = `${process.env.REACT_APP_API_URL}/api/admin`;

function buildHeaders(options) {
  const isFormData = options.body instanceof FormData;
  const headers = {
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (keycloak.token) {
    headers.Authorization = `Bearer ${keycloak.token}`;
  }

  return { ...headers, ...(options.headers || {}) };
}

async function refreshToken() {
  if (!keycloak.token) {
    login();
    throw new Error("Not authenticated");
  }

  try {
    await keycloak.updateToken(30);
  } catch {
    login();
    throw new Error("Session expired");
  }
}

async function authFetch(url, options = {}) {
  await refreshToken();

  const fetchOptions = { cache: "no-store", ...options };
  let res = await fetch(url, {
    ...fetchOptions,
    headers: buildHeaders(options),
  });

  if (res.status === 401) {
    // Token might have been rejected by the server slightly before the client
    // considered it expired: refresh once and retry the request.
    await refreshToken();
    res = await fetch(url, {
      ...fetchOptions,
      headers: buildHeaders(options),
    });
  }

  return res;
}

// ==================== PAPERS ====================

export async function fetchPaper(id) {
  const res = await authFetch(`${API_BASE}/papers/${id}`);
  if (!res.ok) throw new Error("Failed to fetch paper");
  return res.json();
}

export async function fetchPapers() {
  const res = await authFetch(`${API_BASE}/papers`);
  if (!res.ok) throw new Error("Failed to fetch papers");
  return res.json();
}

export async function createPaper(data) {
  const res = await authFetch(`${API_BASE}/papers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create paper");
  return res.json();
}

export async function updatePaper(id, data) {
  const res = await authFetch(`${API_BASE}/papers/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update paper");
  return res.json();
}

export async function replacePaperOriginal(id, file) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await authFetch(`${API_BASE}/papers/${id}/replace-original`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to replace paper original image");
  return res.json();
}

// ==================== BLOCKS ====================

export async function fetchBlocks(paperId) {
  const url = paperId
    ? `${API_BASE}/blocks/paper/${paperId}`
    : `${API_BASE}/blocks`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error("Failed to fetch blocks");
  return res.json();
}

export async function createBlock(data) {
  const res = await authFetch(`${API_BASE}/blocks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create block");
  return res.json();
}

export async function updateBlock(id, data) {
  const res = await authFetch(`${API_BASE}/blocks/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update block");
  return res.json();
}

export async function deleteBlock(id) {
  const res = await authFetch(`${API_BASE}/blocks/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete block");
  return res.json();
}

export async function reorderBlocks(blockOrders) {
  const res = await authFetch(`${API_BASE}/blocks/reorder`, {
    method: "POST",
    body: JSON.stringify({ blockOrders }),
  });
  if (!res.ok) throw new Error("Failed to reorder blocks");
  return res.json();
}

// ==================== IMAGES ====================

export async function updateImage(id, data) {
  const res = await authFetch(`${API_BASE}/images/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update image");
  return res.json();
}

export async function deleteImage(id) {
  const res = await authFetch(`${API_BASE}/images/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete image");
  return res.json();
}

export async function uploadImage(file, title, blockId) {
  const formData = new FormData();
  formData.append("image", file);
  if (title) formData.append("title", title);
  if (blockId) formData.append("blockId", blockId);

  const res = await authFetch(`${API_BASE}/upload/image`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to upload image");
  return res.json();
}

export async function replaceImage(id, file) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await authFetch(`${API_BASE}/images/${id}/replace`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to replace image");
  return res.json();
}

export async function replaceBlockOriginal(id, file) {
  const formData = new FormData();
  formData.append("image", file);

  const res = await authFetch(`${API_BASE}/blocks/${id}/replace-original`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to replace block original image");
  return res.json();
}

// ==================== LISTS ====================

export async function createList(data) {
  const res = await authFetch(`${API_BASE}/lists`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create list");
  return res.json();
}

export async function updateList(id, data) {
  const res = await authFetch(`${API_BASE}/lists/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update list");
  return res.json();
}

export async function deleteList(id) {
  const res = await authFetch(`${API_BASE}/lists/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete list");
  return res.json();
}

// ==================== BLOCK-IMAGES ====================

export async function fetchBlockImages(blockId) {
  const url = blockId
    ? `${API_BASE}/block-images/${blockId}`
    : `${API_BASE}/block-images`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error("Failed to fetch block-images");
  return res.json();
}

export async function createBlockImage(data) {
  const res = await authFetch(`${API_BASE}/block-images`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create block-image relation");
  return res.json();
}

export async function deleteBlockImage(blockId, imageId) {
  const res = await authFetch(
    `${API_BASE}/block-images/${blockId}/${imageId}`,
    {
      method: "DELETE",
    },
  );
  if (!res.ok) throw new Error("Failed to delete block-image relation");
  return res.json();
}

export async function reorderBlockImages(blockId, imageOrders) {
  const res = await authFetch(`${API_BASE}/block-images/reorder`, {
    method: "POST",
    body: JSON.stringify({ blockId, imageOrders }),
  });
  if (!res.ok) throw new Error("Failed to reorder block-images");
  return res.json();
}

// ==================== BLOCK-LISTS ====================

export async function fetchBlockLists(blockId) {
  const url = blockId
    ? `${API_BASE}/block-lists/${blockId}`
    : `${API_BASE}/block-lists`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error("Failed to fetch block-lists");
  return res.json();
}

export async function createBlockList(data) {
  const res = await authFetch(`${API_BASE}/block-lists`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create block-list relation");
  return res.json();
}

export async function deleteBlockList(blockId, listId) {
  const res = await authFetch(`${API_BASE}/block-lists/${blockId}/${listId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete block-list relation");
  return res.json();
}

export async function reorderBlockLists(blockId, listOrders) {
  const res = await authFetch(`${API_BASE}/block-lists/reorder`, {
    method: "POST",
    body: JSON.stringify({ blockId, listOrders }),
  });
  if (!res.ok) throw new Error("Failed to reorder block-lists");
  return res.json();
}

// ==================== AUDIT LOG ====================

export async function fetchAuditLog(limit = 100, offset = 0) {
  const res = await authFetch(
    `${API_BASE}/audit-log?limit=${limit}&offset=${offset}`,
  );
  if (!res.ok) throw new Error("Failed to fetch audit log");
  return res.json();
}

export async function restoreAuditLogEntry(id) {
  const res = await authFetch(`${API_BASE}/audit-log/${id}/restore`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(
      data.message || "Failed to restore audit log entry",
    );
    error.statusCode = res.status;
    error.code = data.error;
    error.data = data;
    throw error;
  }
  return data;
}

export async function restoreAuditLogGroup(changeGroupId) {
  const res = await authFetch(
    `${API_BASE}/audit-log/groups/${encodeURIComponent(changeGroupId)}/restore`,
    { method: "POST" },
  );
  const data = await res.json();
  if (!res.ok) {
    const error = new Error(
      data.message || "Failed to restore audit log group",
    );
    error.statusCode = res.status;
    error.code = data.error;
    error.data = data;
    throw error;
  }
  return data;
}
